import { IListingComponent } from '@models/IListingComponent';
import { IListingFilter } from '@models/IListingFilter';
import { IListingResponse } from '@models/IListingResponse';
import { IStoredFilters } from '@models/IStoredFilters';
import ArticleLayoutsEnum from '@models/enums/ArticleLayoutsEnum';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import useListingStore from '@state/ListingStore';
import convertValuesToArrays from '@utils/helpers/convertValuesToArrays';
import updateDataLayer from '@utils/helpers/gtm';
import mapToDataLayerFilters from '@utils/helpers/mapToDataLayerFilters';
import removeFalsyValues from '@utils/helpers/removeFalsyValues';
import axios from 'axios';
import qs from 'qs';
import { useEffect, useState } from 'react';

interface Props extends IListingComponent {
  readonly endpoint: string;
  readonly layoutType?: ArticleLayoutsEnum;
  readonly persistingParams?: IStoredFilters;
  readonly preFilterParams?: IStoredFilters;
  readonly searchValue?: string;
  readonly showAllVariants?: boolean;
  readonly listingId?: string;
}

const useListingComponentProps = ({
  defaultSort = '',
  endpoint = '',
  facetsToShow = [],
  filtersApplyLabel = '',
  filtersApplySingleLabel = '',
  filtersClearLabel = '',
  filtersCloseLabel = '',
  filtersTitleLabel = '',
  filtersToggleLabel = '',
  language = '',
  listingId = '',
  loadMoreLabel = '',
  noResultsCTALabel = '',
  noResultsText = '',
  errorText = '',
  pageSize = 6,
  persistingParams = {},
  preFilterParams = {},
  searchValue = '',
  showAllVariants = false,
  showFacets = false,
  showKitsOnlyLabel = '',
  sortByLabel = '',
  totalNumberKitsLabel = '',
  totalNumberResultsLabel = '',
  singleResultLabel = '',
  singleKitLabel = '',
  viewedLabel = '',
}: Props) => {
  const [appliedFilters, setAppliedFilters] = useState<IStoredFilters>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [totalFacetsToApply, setTotalFacetsToApply] = useState<number>(0);
  const [showOnlyKits, setShowOnlyKits] = useState<boolean>(false);
  const [sortByParam, setSortByParam] = useState<string>(defaultSort || '');

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const {
    results,
    fetchResults,
    filters,
    loading,
    error,
    total,
    hasNextPage,
    sortOptions,
    didYouMean,
  } = useListingStore();

  const areThereFiltersAvailable = filters?.some(
    (filter: IListingFilter) => filter?.values?.length > 0
  );

  const parseFilters = (
    filterInput: Record<string, string[] | string | number | boolean>
  ) => removeFalsyValues(convertValuesToArrays(filterInput));

  const fetchResultsHandler = (
    params = appliedFilters,
    page = pageNumber,
    sortBy = sortByParam,
    showKits = showOnlyKits
  ) => {
    fetchResults({
      params: parseFilters(params),
      allowedParams: facetsToShow,
      endpoint,
      language,
      listingId,
      pageSize,
      page,
      sortByParam: sortBy,
      showOnlyKits: showKits,
      from: pageSize * page,
      persistingParams: parseFilters(persistingParams) as IStoredFilters,
      showAllVariants,
    });

    if (page === 0) {
      setPageNumber(0);
    }

    if (params && params !== appliedFilters) {
      setAppliedFilters(parseFilters(params) as IStoredFilters);
    }
  };

  const sortHandler = (sortBy: string) => {
    fetchResultsHandler(appliedFilters, 0, sortBy, showOnlyKits);
    setSortByParam(sortBy);
  };

  const showKitsHandler = (showKits: boolean) => {
    fetchResultsHandler(appliedFilters, 0, sortByParam, showKits);
    setShowOnlyKits(showKits);
  };

  const paginationHandler = (page: number) => {
    fetchResultsHandler(appliedFilters, page, sortByParam, showOnlyKits);
    setPageNumber(page);
  };

  const updateHrefWithQueryParams = (
    queryValue:
      | IStoredFilters
      | {
          query?: string;
        }
  ) => {
    if (typeof window !== 'undefined') {
      const newQueryValue = qs.stringify(queryValue, {
        arrayFormat: 'repeat',
      });
      const newHref = `${window.location.pathname}${
        newQueryValue ? `?${newQueryValue}` : ''
      }`;

      window.history.pushState({ path: newHref }, '', newHref);
    }
  };

  const applyFilters = (filtersToApply: IStoredFilters) => {
    updateDataLayer({
      event: 'filterUsed',
      content_type: pageObject?.pageType,
      filter_action: 'apply_filter',
      filter: mapToDataLayerFilters(filters, filtersToApply),
    });

    const newQueryValue = parseFilters(filtersToApply) as IStoredFilters;
    updateHrefWithQueryParams(newQueryValue);
    setIsDrawerOpen(false);
    fetchResultsHandler(filtersToApply, 0);
  };

  const clearFacets = () => {
    updateDataLayer({
      event: 'filterUsed',
      content_type: pageObject?.pageType,
      filter_action: 'clear_all_filter',
      filter: null,
    });
    const newQueryValue = { ...(searchValue ? { query: searchValue } : {}) };
    updateHrefWithQueryParams(newQueryValue);
    setIsDrawerOpen(false);
    fetchResultsHandler(searchValue ? { query: [searchValue] } : {}, 0);
  };

  const fetchTotal = async (currentFilters: IStoredFilters) => {
    const params: Record<string, string | string[] | number | boolean> = {
      facetsToShow,
      language,
      pageSize,
      from: '0',
      showAllVariants,
      showOnlyKits,
      ...parseFilters(persistingParams),
      ...parseFilters(currentFilters),
    };

    try {
      const { data } = await axios.get<IListingResponse | undefined>(endpoint, {
        params: parseFilters(params),
        paramsSerializer: (parameters) =>
          qs.stringify(parameters, {
            arrayFormat: 'repeat',
            encode: false,
          }),
      });

      setTotalFacetsToApply(data?.total || 0);
    } catch {
      setErrorBannerNotification(ErrorBannerNotificationEnum.SearchResults);

      setTotalFacetsToApply(0);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const query = window.location.search;
      const initialFilters = query
        ? qs.parse(query, {
            ignoreQueryPrefix: true,
          }) || {}
        : preFilterParams;
      const convertedInitialFilters = convertValuesToArrays(
        initialFilters
      ) as IStoredFilters;
      fetchResultsHandler(convertedInitialFilters);
    }
  }, []);

  return {
    appliedFilters,
    applyFilters,
    areThereFiltersAvailable,
    clearFacets,
    didYouMean,
    error,
    facetsDisabled: loading || error || !areThereFiltersAvailable, // should look at removing this or consolidating our approach, it's only used in one place
    fetchResultsHandler,
    fetchTotal,
    filters,
    filtersApplyLabel,
    filtersApplySingleLabel,
    filtersClearLabel,
    filtersCloseLabel,
    filtersTitleLabel,
    filtersToggleLabel,
    isDrawerOpen,
    language,
    loading,
    loadMoreLabel,
    noResultsCTALabel,
    noResultsText,
    errorText,
    pageNumber,
    pageSize,
    paginationHandler,
    results,
    setIsDrawerOpen,
    setPageNumber,
    showFacets,
    showKitsHandler,
    showKitsOnlyLabel,
    showOnlyKits,
    sortByLabel,
    sortByParam,
    sortHandler,
    sortOptions,
    hasNextPage,
    total,
    totalFacetsToApply,
    totalNumberKitsLabel,
    totalNumberResults: total,
    totalNumberResultsLabel,
    singleResultLabel,
    singleKitLabel,
    viewedLabel,
  };
};

export default useListingComponentProps;
